import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Result, Button } from 'antd'

interface Props {
  children: ReactNode
  // A key that, when it changes, resets the boundary — used by the router
  // wrapper below to auto-recover on navigation so a broken page doesn't
  // wedge the whole shell after the user clicks away.
  resetKey?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info)
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title="Something went wrong on this page"
          subTitle="An unexpected error occurred. Try refreshing the page."
          extra={
            <Button type="primary" onClick={() => this.setState({ hasError: false, error: null })}>
              Try Again
            </Button>
          }
        />
      )
    }
    return this.props.children
  }
}

/**
 * Route-aware error boundary.
 *
 * Placed inside a Route so it catches render-time crashes in a single page
 * without unmounting the shell (menu/header stay usable). The location
 * pathname doubles as the reset key: when the user navigates away, the
 * boundary clears so the next page mounts cleanly instead of getting stuck
 * on the previous error screen.
 */
export default function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <ErrorBoundaryInner resetKey={location.pathname}>{children}</ErrorBoundaryInner>
}
